import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/siteContent";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.email) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="login-home-link">
          <img src={BRAND.logoDark} alt="Kairos Buildings" className="h-14 w-14 object-contain" />
          <span className="font-heading text-white text-xl font-bold">Kairos Buildings</span>
        </Link>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={18} className="text-blue-700" />
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Admin Login</h1>
          </div>
          <p className="text-slate-500 text-sm mb-6">Manage your quote submissions.</p>

          <form onSubmit={submit} data-testid="login-form" className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@kairosbuildings.ca"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full py-6"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
            </Button>
          </form>
        </div>

        <Link to="/" className="block text-center text-slate-400 text-sm mt-6 hover:text-white">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
